import { useRef, useState } from 'react'
import { Tv, ChevronLeft, ChevronRight } from 'lucide-react'
import { COMPANIONS, type Companion } from '../data/companions'
import { CompanionAvatar } from '@/components/CompanionAvatar'
import { AISparkle } from '@/components/ui/AISparkle'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

const SWIPE_THRESHOLD = 80

export function ChooseCoach({ onChoose, current }: { onChoose: (id: string) => void; current?: string | null }) {
  const startIdx = Math.max(0, COMPANIONS.findIndex((c) => c.id === current))
  const [i, setI] = useState(startIdx)
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  const startX = useRef(0)
  const front = COMPANIONS[i]
  const last = COMPANIONS.length - 1

  const go = (dir: 1 | -1) => setI((v) => Math.min(last, Math.max(0, v + dir)))

  const onDown = (e: React.PointerEvent) => {
    startX.current = e.clientX
    setDragging(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => { if (dragging) setDx(e.clientX - startX.current) }
  const onUp = () => {
    if (!dragging) return
    setDragging(false)
    if (dx < -SWIPE_THRESHOLD && i < last) setI(i + 1)
    else if (dx > SWIPE_THRESHOLD && i > 0) setI(i - 1)
    setDx(0)
  }

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="shrink-0 px-5 pt-7 pb-2 text-center" style={{ paddingTop: 'max(1.75rem, env(safe-area-inset-top))' }}>
        <div className="mb-1 flex items-center justify-center gap-1.5">
          <AISparkle size="sm" />
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Meet your coaches</span>
        </div>
        <h1 className="text-xl font-semibold text-foreground">Who do you want in your corner?</h1>
        <p className="mt-1 text-sm text-muted-foreground">Swipe to meet them. Pick the one you click with — switch anytime.</p>
      </header>

      {/* Card deck */}
      <div className="relative flex-1 px-5 py-3">
        {COMPANIONS.map((c, idx) => {
          const p = idx - i
          if (p < -1 || p > 2) return null
          const isFront = p === 0
          let transform: string
          let opacity = 1
          if (p < 0) { transform = 'translateX(-130%) rotate(-14deg)'; opacity = 0 }
          else if (isFront) transform = `translateX(${dx}px) rotate(${dx * 0.04}deg)`
          else transform = `translateY(${p * 10}px) scale(${1 - p * 0.05})`
          return (
            <article
              key={c.id}
              onPointerDown={isFront ? onDown : undefined}
              onPointerMove={isFront ? onMove : undefined}
              onPointerUp={isFront ? onUp : undefined}
              onPointerCancel={isFront ? onUp : undefined}
              className={cn('absolute inset-x-5 top-3 bottom-3 select-none', isFront && 'cursor-grab active:cursor-grabbing')}
              style={{
                transform,
                opacity,
                zIndex: 10 - Math.abs(p),
                // Front card owns the whole gesture surface so the ENTIRE card swipes (not just the top).
                touchAction: isFront ? 'none' : undefined,
                transition: dragging && isFront ? 'none' : 'transform .38s cubic-bezier(.22,1,.36,1), opacity .3s',
              }}
            >
              <CoachCard c={c} />
            </article>
          )
        })}

        {/* Swipe affordances */}
        {i > 0 && (
          <button onClick={() => go(-1)} className="absolute left-1 top-1/2 z-20 -translate-y-1/2 rounded-full bg-card/80 p-1.5 shadow backdrop-blur hover:bg-card" aria-label="Previous">
            <ChevronLeft className="size-5 text-foreground" />
          </button>
        )}
        {i < last && (
          <button onClick={() => go(1)} className="absolute right-1 top-1/2 z-20 -translate-y-1/2 rounded-full bg-card/80 p-1.5 shadow backdrop-blur hover:bg-card" aria-label="Next">
            <ChevronRight className="size-5 text-foreground" />
          </button>
        )}
      </div>

      {/* Dots */}
      <div className="flex shrink-0 items-center justify-center gap-1.5 pb-2">
        {COMPANIONS.map((c, idx) => (
          <span key={c.id} className={cn('h-1.5 rounded-full transition-all', idx === i ? 'w-5 bg-primary' : 'w-1.5 bg-border')} />
        ))}
      </div>

      <div className="shrink-0 border-t border-border bg-card px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <Button variant="primary" className="w-full" onClick={() => onChoose(front.id)}>
          Start with {front.name}
        </Button>
      </div>
    </div>
  )
}

function CoachCard({ c }: { c: Companion }) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-xl">
      {/* Gradient hero */}
      <div
        className="relative flex flex-col items-center gap-2 px-5 pt-9 pb-6 text-center text-white"
        style={{ backgroundImage: `linear-gradient(160deg, ${c.gradient[0]}, ${c.gradient[1]})` }}
      >
        <span className="rounded-full ring-4 ring-white/35">
          <CompanionAvatar c={c} size={84} />
        </span>
        <h2 className="text-2xl font-bold tracking-tight">{c.name}</h2>
        <div className="text-sm opacity-95">{c.age} · {c.from}</div>
        <div className="mt-1 rounded-full bg-white/20 px-3 py-1 text-xs font-medium backdrop-blur-sm">{c.tagline}</div>
      </div>

      {/* Body — no independent scroll, so the whole card is one swipe surface */}
      <div className="flex flex-1 flex-col gap-3 px-5 py-4">
        <p className="text-sm leading-relaxed text-foreground">{c.blurb}</p>
        <div className="flex flex-wrap gap-1.5">
          {c.traits.map((t) => (
            <span key={t} className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">{t}</span>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Tv className="size-3.5" /> {c.watches}
        </div>
        <p className="mt-auto rounded-2xl bg-muted/60 px-3.5 py-2.5 text-sm italic leading-relaxed text-foreground">{c.preview}</p>
      </div>
    </div>
  )
}
